import { useIsMutating } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Endpoint } from '@/Config/api/endpoint';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';

export const SavingBadge = () => {
  const { t } = useTranslation('paragraphs');

  const [isSaving, setIsSaving] = useState(false);
  const timeout = useRef<NodeJS.Timeout>();

  const isMutating = useIsMutating({ mutationKey: [Endpoint.knowledgeBase.articles.update] });

  useEffect(() => {
    if (isMutating > 0) {
      setIsSaving(true);
    } else {
      timeout.current = setTimeout(() => {
        setIsSaving(false);
      }, 2000);
    }

    return () => clearTimeout(timeout.current);
  }, [isMutating]);

  return (
    <Badge
      variant={isSaving ? 'secondary' : 'success'}
      className="w-18 place-content-center !mt-0 mx-auto whitespace-nowrap"
      size="lg"
    >
      <AnimatePresence mode="wait" initial={false}>
        {isSaving ? (
          <motion.span
            key="saving"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <div className="mr-2 inline-flex size-2 animate-pulse rounded-full bg-foreground-subtle" />
            {t('modal.badge.saving')}
          </motion.span>
        ) : (
          <motion.span
            key="saved"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <div className="mr-2 inline-flex size-2 rounded-full bg-success" />
            {t('modal.badge.saved')}
          </motion.span>
        )}
      </AnimatePresence>
    </Badge>
  );
};
